import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { pickPlanForFeature, SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { titleCaseLabel } from '../../../common/views/SMLButton/titleCaseLabel';
import styles from './LockedControl.module.scss';

export type LockedControlVariant = 'button' | 'menuItem' | 'link' | 'chip';

interface LockedControlProps {
  /** Design n draws three shapes: a button (its own label kept, a lock glyph added), a menu item
   * (icon, label, chip in a dropdown row) and a link (an "Upgrade" word next to explanatory text).
   * `chip` is the bare lock chip on its own — a value cell in a table row (design r1) that names
   * the cheapest plan and opens the prompt, with no label of its own next to it. */
  variant: LockedControlVariant;
  /** Names the plan lock and the prompt this control opens — the chip reads the cheapest plan
   * that unlocks it (`pickPlanForFeature`), and a click hands the same feature to the caller. */
  feature: SMLUpgradeFeature;
  /** The control's own label. Unchanged from the unlocked state — locking never renames a button.
   * `chip` never shows it, but still uses it as the accessible name when `ariaLabel` is left out. */
  label: React.ReactNode;
  /** Opens the global upgrade prompt for `feature` (`useUpgradePrompt`). Left out for `menuItem`,
   * whose click belongs to the row that wraps it (a `DropdownItem`). */
  onClick?: () => void;
  /** `button` only: a smaller control, for a row that already reads as compact (e.g. Publish). */
  size?: 'sm';
  /** Design n's separate "Disabled" state: a request is already in flight, so the action cannot
   * run right now. No chip, 45% opacity, not clickable — distinct from the lock itself, which
   * always stays clickable. */
  disabled?: boolean;
  /** `button` only: a string label is title-cased the same way `SMLButton` does, so a locked
   * button reads the same as the `SMLButton` it stands in for. Set this when the control it
   * replaces never capitalises its own label (e.g. the primitives `SMLButton`), so the two
   * states keep matching. */
  omitCap?: boolean;
  className?: string;
  ariaLabel?: string;
}

interface LockChipProps {
  /** The chip names the cheapest plan that unlocks this feature (`pickPlanForFeature`). */
  feature: SMLUpgradeFeature;
  className?: string;
  /**
   * Adds a spoken sentence ("Needs the Artist plan") and hides the bare plan name from assistive
   * tech. Use it where the chip sits inside a bigger control, such as a navigation tile, and
   * would otherwise read as a lone plan name.
   */
  withSpokenHint?: boolean;
}

/**
 * Design n: the lock chip on its own, not interactive. It sits inside a control that owns the
 * click (a menu row, a link, a navigation tile). The clickable variants below use it too, so the
 * chip has one look everywhere.
 */
const LockChipComponent: React.FC<LockChipProps> = ({ feature, className, withSpokenHint = false }) => {
  const intl = useIntl();
  const currentCapabilities = useMusic24Capabilities();
  const planName = intl.formatMessage(planNameMessages[pickPlanForFeature(feature, currentCapabilities).tier]);

  return (
    <>
      <span
        className={clsx(styles.chip, className)}
        aria-hidden={withSpokenHint ? true : undefined}
        data-testid="lock-chip"
      >
        <FontAwesomeIcon icon={faLock} className={styles.chipIcon} />
        {planName}
      </span>
      {withSpokenHint && (
        <span className="visually-hidden">
          {intl.formatMessage(
            { id: 'app.sml.lockChip.needsPlan', defaultMessage: 'Needs the {plan} plan' },
            { plan: planName }
          )}
        </span>
      )}
    </>
  );
};

export const LockChip = TypedMemo(LockChipComponent);

/**
 * Design n: one look for every locked control. A lock glyph, the control's own label, and a chip
 * naming the cheapest plan that unlocks it. The control stays clickable — the lock itself is
 * never shown as disabled — and the click opens the global upgrade prompt (design b1) for its
 * feature. `disabled` is a separate, unrelated state (an in-flight request), not the lock.
 */
const LockedControlComponent: React.FC<LockedControlProps> = ({
  variant,
  feature,
  label,
  onClick,
  size,
  disabled = false,
  omitCap = false,
  className,
  ariaLabel,
}) => {
  const intl = useIntl();
  const currentCapabilities = useMusic24Capabilities();
  const plan = pickPlanForFeature(feature, currentCapabilities);

  const chip = disabled ? null : <LockChip feature={feature} />;

  if (variant === 'chip') {
    return (
      <button
        type="button"
        className={clsx(styles.chipButton, className)}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      >
        <FontAwesomeIcon icon={faLock} className={styles.chipIcon} />
        {intl.formatMessage(planNameMessages[plan.tier])}
      </button>
    );
  }

  if (variant === 'menuItem') {
    return (
      <span className={clsx(styles.menuItem, disabled && styles.disabled, className)}>
        <FontAwesomeIcon icon={faLock} className={styles.menuItemIcon} />
        <span className={styles.menuItemLabel}>{label}</span>
        {chip}
      </span>
    );
  }

  if (variant === 'link') {
    return (
      <span className={clsx(styles.linkRow, className)}>
        <button
          type="button"
          className={styles.link}
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          aria-label={ariaLabel}
        >
          {label}
        </button>
        {chip}
      </span>
    );
  }

  return (
    <span className={clsx(styles.buttonRow, className)}>
      <button
        type="button"
        className={clsx(styles.button, size === 'sm' && styles.sm)}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <FontAwesomeIcon icon={faLock} className={styles.buttonIcon} />
        {typeof label === 'string' && !omitCap ? titleCaseLabel(label, intl.locale) : label}
      </button>
      {chip}
    </span>
  );
};

export const LockedControl = TypedMemo(LockedControlComponent);
